'use client';
import { Header } from '@/ui-components/organisms/Header/Header';
import { Grid } from '@/ui-components/atoms/Layout/Grid/Grid';
import { Text } from '@/ui-components/atoms/Text/Text';
import { StyledContainer } from '@/ui-components/atoms/Layout/StyledContainer/StyledContainer';
import { RenderRTE } from '@/components-2.0/structure/RenderRTE';
import { FlexibleFormVariant } from './FlexibleFormBase';
import {
    FlexibleFormVariantFactoryArgs,
    leftAlignedHeaderStyles,
    renderCenteredFlexibleFormContent,
    renderNarrowFlexibleFormContent,
} from './flexibleFormVariantHelpers';
import styles from './flexibleForm.module.scss';

const {
    descriptionTextStyles,
    flexForm,
    halfWidth,
    fullWidth,
    formSectionHeaderStyles,
    formSectionTextBlockStyles,
    toggleFormTextBlockStyles,
    dropdownSelectStyles,
    submitButtonWrapperRight,
    wrapperRow,
    stateContainer,
    stateWrapper,
    zipInputStyles,
    selectorStyles,
    duplicationFormError,
    ctaCaption,
} = styles;

export function createDefaultFlexibleFormVariant({
    asset,
    nestedForm,
    toggleForm,
    hasHeadlineContent,
    alignment,
    headerEyebrow,
}: FlexibleFormVariantFactoryArgs): Pick<FlexibleFormVariant, 'styles' | 'submitButtonFitting' | 'renderContent'> {
    return {
        styles: {
            flexForm,
            halfWidth,
            fullWidth,
            formSectionHeaderStyles,
            formSectionTextBlockStyles: toggleForm ? toggleFormTextBlockStyles : formSectionTextBlockStyles,
            inputStyles: alignment === 'Narrow' ? styles.narrowInput : undefined,
            dropdownSelectStyles,
            wrapperRow,
            stateContainer,
            stateWrapper,
            zipInputStyles,
            selectorStyles,
        },
        submitButtonFitting: toggleForm || alignment === 'Narrow' ? 'fill-container' : 'hug-contents',
        renderContent: (contentArgs) =>
            alignment === 'Narrow'
                ? renderNarrowFlexibleFormContent(
                      { asset, nestedForm, toggleForm, hasHeadlineContent, alignment, headerEyebrow, formClassName: flexForm },
                      contentArgs,
                  )
                : alignment === 'Centre'
                ? renderCenteredFlexibleFormContent(
                      {
                          asset,
                          nestedForm,
                          toggleForm,
                          hasHeadlineContent,
                          alignment,
                          formClassName: flexForm,
                      },
                      contentArgs,
                  )
                : (
                      <Grid container>
                          {hasHeadlineContent && !toggleForm && (
                              <Grid col={{ sm: 12, md: 6 }} standardMoleculeSpacing>
                                  <Header
                                      headingTag="h2"
                                      heading={<RenderRTE contentfulDoc={asset.fields.headline} />}
                                      theme="left"
                                      className={leftAlignedHeaderStyles}
                                  />
                              </Grid>
                          )}
                          <Grid col={{ sm: 12, md: 6 }}>
                              <Text tag="p" className={descriptionTextStyles}>
                                  <RenderRTE contentfulDoc={asset.fields.description} />
                              </Text>
                              <Grid col={{ sm: 12, lg: 6 }}>
                                  <div className={flexForm}>{contentArgs.wrappedFieldElements}</div>
                              </Grid>
                              <div className={submitButtonWrapperRight}>
                                  {contentArgs.duplicateError && (
                                      <StyledContainer>
                                          <Text tag="div" className={duplicationFormError} role="alert">
                                              {contentArgs.duplicateErrorMessage}
                                          </Text>
                                      </StyledContainer>
                                  )}
                                  {contentArgs.submitButton}
                                  {asset.fields.cta_caption && (
                                      <RenderRTE contentfulDoc={asset.fields.cta_caption} className={ctaCaption} />
                                  )}
                              </div>
                          </Grid>
                      </Grid>
                  ),
    };
}
